import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Layout } from '../components/Layout';
import { useIsMobile } from '../hooks/useMediaQuery';
import {
  getAgents,
  getConversations,
  getConversation,
  createConversation,
  sendMessage,
  deleteConversation,
  exportConversationPdf,
  type Agent,
  type Conversation,
  type Message,
  type ChatAttachment,
  type LLMInfo,
} from '../api/chat';
import { getVisionStatus, type VisionStatus } from '../api/user-settings';

const AGENT_ICONS: Record<string, string> = {
  coordinator: '🧠',
  oncologist_colon: '🔬',
  oncologist_prostate: '🛡️',
  oncologist_lung: '🌬️',
  oncologist_bone: '🦴',
  cardiologist: '❤️',
  urologist: '🫘',
  pulmonologist: '🫁',
  orthopedist: '🦿',
  ophthalmologist: '👁️',
  gastroenterologist: '🔬',
  dermatologist: '🧴',
  rheumatologist: '🦠',
  orthodontist: '🦷',
  otolaryngologist: '👂',
  nutritionist: '🍎',
  trainer: '💪',
  longevity: '⏳',
  pharmacist: '💊',
};

const AGENT_COLORS: Record<string, string> = {
  coordinator: 'bg-blue-500',
  oncologist_colon: 'bg-purple-500',
  oncologist_prostate: 'bg-purple-500',
  oncologist_lung: 'bg-purple-500',
  oncologist_bone: 'bg-purple-500',
  cardiologist: 'bg-red-500',
  urologist: 'bg-amber-500',
  pulmonologist: 'bg-sky-500',
  orthopedist: 'bg-slate-500',
  ophthalmologist: 'bg-cyan-500',
  gastroenterologist: 'bg-lime-600',
  dermatologist: 'bg-pink-500',
  rheumatologist: 'bg-violet-500',
  orthodontist: 'bg-stone-500',
  otolaryngologist: 'bg-indigo-500',
  nutritionist: 'bg-green-500',
  trainer: 'bg-orange-500',
  longevity: 'bg-teal-500',
  pharmacist: 'bg-rose-500',
};

// Short labels for quick start buttons
const AGENT_SHORT_LABELS: Record<string, string> = {
  coordinator: 'Coordinatore',
  oncologist_prostate: 'Prostata',
  oncologist_colon: 'Colon',
  oncologist_lung: 'Polmone',
  oncologist_bone: 'Ossa',
  cardiologist: 'Cuore',
  urologist: 'Urologia',
  pulmonologist: 'Pneumologia',
  orthopedist: 'Ortopedia',
  ophthalmologist: 'Oculistica',
  gastroenterologist: 'Gastro',
  dermatologist: 'Dermatologia',
  rheumatologist: 'Reumatologia',
  orthodontist: 'Ortodonzia',
  otolaryngologist: 'ORL',
  nutritionist: 'Nutrizione',
  trainer: 'Trainer',
  longevity: 'Longevità',
  pharmacist: 'Farmacia',
};

export function Chat() {
  const navigate = useNavigate();
  const { conversationId } = useParams<{ conversationId: string }>();
  const isMobile = useIsMobile();

  const [agents, setAgents] = useState<Agent[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [messageMenuId, setMessageMenuId] = useState<number | null>(null);
  const [visionWarning, setVisionWarning] = useState<VisionStatus | null>(null);
  const [showVisionWarning, setShowVisionWarning] = useState(false);
  const [lastLLMInfo, setLastLLMInfo] = useState<LLMInfo | null>(null);
  const [agentFilter, setAgentFilter] = useState('');

  // Mobile sidebar visibility
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  // Resizable sidebar (desktop only)
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    const saved = localStorage.getItem('chat_sidebar_width');
    return saved ? parseInt(saved, 10) : 320;
  });
  const [isResizing, setIsResizing] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Load agents and conversations on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [agentsRes, convsRes] = await Promise.all([
          getAgents(),
          getConversations(),
        ]);
        if (agentsRes.success) setAgents(agentsRes.agents);
        if (convsRes.success) setConversations(convsRes.conversations);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, []);

  // Load conversation when ID changes
  useEffect(() => {
    const loadConversation = async () => {
      if (!conversationId) {
        setCurrentConversation(null);
        setMessages([]);
        return;
      }

      setIsLoading(true);
      try {
        const res = await getConversation(parseInt(conversationId, 10));
        if (res.success) {
          setCurrentConversation(res.conversation);
          setMessages(res.conversation.messages || []);
        }
      } catch (error) {
        console.error('Error loading conversation:', error);
        navigate('/chat');
      } finally {
        setIsLoading(false);
      }
    };
    loadConversation();
  }, [conversationId, navigate]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when conversation changes
  useEffect(() => {
    if (currentConversation) {
      inputRef.current?.focus();
    }
  }, [currentConversation]);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
      // Close message menu if clicked outside
      const target = event.target as Element;
      if (!target.closest('.message-menu-container')) {
        setMessageMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check vision status when images are attached
  useEffect(() => {
    const hasImages = attachments.some(a => a.type === 'image');
    if (hasImages) {
      getVisionStatus().then(res => {
        if (res.success && !res.willWork) {
          setVisionWarning(res);
        } else {
          setVisionWarning(null);
        }
      }).catch(() => {
        setVisionWarning(null);
      });
    } else {
      setVisionWarning(null);
    }
  }, [attachments]);

  // Handle sidebar resize
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const newWidth = Math.min(Math.max(e.clientX, 200), 600); // Min 200px, max 600px
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      if (isResizing) {
        setIsResizing(false);
        localStorage.setItem('chat_sidebar_width', sidebarWidth.toString());
      }
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, sidebarWidth]);

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  // Export functions
  const formatMessagesAsText = (msgs: Message[]): string => {
    const agentName = agents.find(a => a.id === currentConversation?.agentType)?.name || currentConversation?.agentType || 'Assistente';
    let text = `Conversazione: ${currentConversation?.title || 'Chat'}\n`;
    text += `Agente: ${agentName}\n`;
    text += `Data: ${new Date().toLocaleDateString('it-IT')}\n`;
    text += '─'.repeat(50) + '\n\n';

    msgs.forEach((msg) => {
      const role = msg.role === 'user' ? 'Tu' : (agents.find(a => a.id === msg.agentType)?.name || 'Assistente');
      const time = new Date(msg.createdAt).toLocaleString('it-IT');
      text += `[${time}] ${role}:\n${msg.content}\n\n`;
    });

    return text;
  };

  const formatMessagesAsMarkdown = (msgs: Message[]): string => {
    const agentName = agents.find(a => a.id === currentConversation?.agentType)?.name || currentConversation?.agentType || 'Assistente';
    let md = `# ${currentConversation?.title || 'Chat'}\n\n`;
    md += `**Agente:** ${agentName}  \n`;
    md += `**Data:** ${new Date().toLocaleDateString('it-IT')}\n\n`;
    md += '---\n\n';

    msgs.forEach((msg) => {
      const role = msg.role === 'user' ? '👤 Tu' : `🤖 ${agents.find(a => a.id === msg.agentType)?.name || 'Assistente'}`;
      const time = new Date(msg.createdAt).toLocaleString('it-IT');
      md += `### ${role}\n`;
      md += `*${time}*\n\n`;
      md += `${msg.content}\n\n`;
      md += '---\n\n';
    });

    return md;
  };

  const handleCopyAsText = async () => {
    if (!messages.length) return;
    const text = formatMessagesAsText(messages);
    await navigator.clipboard.writeText(text);
    setShowExportMenu(false);
    alert('Conversazione copiata come testo!');
  };

  const handleCopyAsMarkdown = async () => {
    if (!messages.length) return;
    const md = formatMessagesAsMarkdown(messages);
    await navigator.clipboard.writeText(md);
    setShowExportMenu(false);
    alert('Conversazione copiata come Markdown!');
  };

  const handleDownloadMarkdown = () => {
    if (!messages.length) return;
    const md = formatMessagesAsMarkdown(messages);
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentConversation?.title || 'chat'}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
  };

  const handleExportPdf = async () => {
    if (!currentConversation || !messages.length) return;
    setIsExporting(true);
    try {
      const blob = await exportConversationPdf(currentConversation.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${currentConversation.title || 'chat'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setShowExportMenu(false);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Errore nell\'esportazione PDF');
    } finally {
      setIsExporting(false);
    }
  };

  // Export single message
  const handleCopyMessage = async (msg: Message, format: 'text' | 'markdown') => {
    const role = msg.role === 'user' ? 'Tu' : (agents.find(a => a.id === msg.agentType)?.name || 'Assistente');
    const time = new Date(msg.createdAt).toLocaleString('it-IT');

    let content: string;
    if (format === 'text') {
      content = `[${time}] ${role}:\n${msg.content}`;
    } else {
      content = `### ${msg.role === 'user' ? '👤 Tu' : '🤖 ' + role}\n*${time}*\n\n${msg.content}`;
    }

    await navigator.clipboard.writeText(content);
    alert('Messaggio copiato!');
  };

  const handleNewChat = async (agentType: string) => {
    try {
      const res = await createConversation(agentType);
      if (res.success) {
        setConversations((prev) => [res.conversation, ...prev]);
        setShowNewChat(false);
        setShowMobileSidebar(false); // Close mobile sidebar after creating chat
        navigate(`/chat/${res.conversation.id}`);
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newAttachments: ChatAttachment[] = [];

    for (const file of Array.from(files)) {
      const isImage = file.type.startsWith('image/');

      if (isImage) {
        // Read image as base64
        const reader = new FileReader();
        const base64 = await new Promise<string>((resolve) => {
          reader.onload = () => {
            const result = reader.result as string;
            // Remove data URL prefix
            resolve(result.split(',')[1]);
          };
          reader.readAsDataURL(file);
        });
        newAttachments.push({
          filename: file.name,
          type: 'image',
          content: base64,
          mimeType: file.type,
        });
      } else if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
        // For PDFs, we send as text - backend will parse
        newAttachments.push({
          filename: file.name,
          type: 'text',
          content: `[PDF: ${file.name}]`,
          mimeType: file.type,
        });
      } else {
        // Text files
        const text = await file.text();
        newAttachments.push({
          filename: file.name,
          type: 'text',
          content: text,
        });
      }
    }

    setAttachments((prev) => [...prev, ...newAttachments]);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSendMessage = async (bypassVisionWarning = false) => {
    if ((!inputValue.trim() && attachments.length === 0) || !currentConversation || isSending) return;

    // Check if we need to show vision warning
    const hasImages = attachments.some(a => a.type === 'image');
    if (hasImages && visionWarning && !bypassVisionWarning) {
      setShowVisionWarning(true);
      return;
    }

    const content = inputValue.trim() || 'Analizza gli allegati';
    const currentAttachments = [...attachments];
    const isEditing = editingMessageId !== null;

    setInputValue('');
    setAttachments([]);
    setEditingMessageId(null);
    setIsSending(true);

    // If editing, remove all messages from the edited message onwards
    if (isEditing) {
      const editIndex = messages.findIndex((m) => m.id === editingMessageId);
      if (editIndex !== -1) {
        setMessages((prev) => prev.slice(0, editIndex));
      }
    }

    // Optimistically add user message
    const tempUserMessage: Message = {
      id: Date.now(),
      role: 'user',
      content: currentAttachments.length > 0
        ? `${content}\n\n📎 ${currentAttachments.map(a => a.filename).join(', ')}`
        : content,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempUserMessage]);

    try {
      const res = await sendMessage(
        currentConversation.id,
        content,
        currentAttachments.length > 0 ? currentAttachments : undefined
      );
      if (res.success) {
        // Replace temp message with real one and add assistant response
        setMessages((prev) => [
          ...prev.filter((m) => m.id !== tempUserMessage.id),
          res.userMessage,
          res.assistantMessage,
        ]);
        // Update conversation in sidebar
        setConversations((prev) =>
          prev.map((c) =>
            c.id === currentConversation.id
              ? { ...c, lastMessage: res.assistantMessage.content.substring(0, 100) }
              : c
          )
        );
        // Track LLM info for fallback indicator
        if (res.llmInfo) {
          setLastLLMInfo(res.llmInfo);
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove optimistic message on error
      setMessages((prev) => prev.filter((m) => m.id !== tempUserMessage.id));
      setInputValue(content); // Restore input
      setAttachments(currentAttachments); // Restore attachments
      if (isEditing) {
        // Reload conversation to restore messages
        const res = await getConversation(currentConversation.id);
        if (res.success) {
          setMessages(res.conversation.messages || []);
        }
      }
    } finally {
      setIsSending(false);
      inputRef.current?.focus();
    }
  };

  const handleDeleteConversation = async (id: number) => {
    if (!confirm('Eliminare questa conversazione?')) return;
    try {
      await deleteConversation(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (currentConversation?.id === id) {
        navigate('/chat');
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleEditMessage = (msg: Message) => {
    // Extract just the text content (remove attachment info)
    const content = msg.content.split('\n\n📎')[0];
    setInputValue(content);
    setEditingMessageId(msg.id);
    inputRef.current?.focus();
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setInputValue('');
  };

  return (
    <Layout title="Consulta">
      <div className="flex h-[calc(100vh-140px)] -mx-4 sm:-mx-6 lg:-mx-8 -my-8 lg:-my-12">
        {/* Mobile sidebar overlay backdrop */}
        {isMobile && showMobileSidebar && (
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowMobileSidebar(false)}
          />
        )}

        {/* Conversations sidebar */}
        <div
          ref={sidebarRef}
          className={`bg-white border-r border-gray-200 flex flex-col overflow-hidden flex-shrink-0 ${
            isMobile
              ? `fixed inset-y-0 left-0 z-50 w-80 transition-transform duration-300 ${
                  showMobileSidebar ? 'translate-x-0' : '-translate-x-full'
                }`
              : ''
          }`}
          style={isMobile ? undefined : { width: sidebarWidth }}
        >
          {/* Mobile sidebar header with close button */}
          {isMobile && (
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h2 className="font-semibold text-lg text-gray-900">Conversazioni</h2>
              <button
                onClick={() => setShowMobileSidebar(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          <div className={`p-4 ${isMobile ? '' : 'border-b border-gray-100'}`}>
            <button
              onClick={() => setShowNewChat(true)}
              className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-base lg:text-lg font-medium transition-colors"
            >
              + Nuova Chat
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <p className="text-gray-400 text-base lg:text-lg p-5 text-center">
                Nessuna conversazione
              </p>
            ) : (
              <div className="p-2 space-y-1">
                {conversations.map((conv) => (
                  <div
                    key={conv.id}
                    className={`group flex items-center gap-3 p-3 lg:p-4 rounded-xl cursor-pointer transition-colors ${
                      currentConversation?.id === conv.id
                        ? 'bg-blue-50 border border-blue-200'
                        : 'hover:bg-gray-50'
                    }`}
                    onClick={() => {
                      navigate(`/chat/${conv.id}`);
                      if (isMobile) setShowMobileSidebar(false);
                    }}
                  >
                    <span className="text-xl lg:text-2xl">
                      {AGENT_ICONS[conv.agentType] || '💬'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-base lg:text-lg font-medium text-gray-900 truncate">{conv.title}</p>
                      <p className="text-sm lg:text-base text-gray-500 truncate">
                        {conv.lastMessage || 'Nessun messaggio'}
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteConversation(conv.id);
                      }}
                      className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 p-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Resize handle (desktop only) */}
        {!isMobile && (
          <div
            onMouseDown={handleResizeStart}
            className={`w-1 bg-gray-200 hover:bg-blue-400 cursor-col-resize flex-shrink-0 transition-colors ${
              isResizing ? 'bg-blue-500' : ''
            }`}
            title="Trascina per ridimensionare"
          />
        )}

        {/* Main chat area */}
        <div className="flex-1 flex flex-col bg-gray-50 min-w-0 overflow-hidden">
          {/* Quick start bar - always visible */}
          <div className="bg-white border-b border-gray-100 px-4 lg:px-6 py-2 flex-shrink-0">
            {/* Filter input */}
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm text-gray-500 whitespace-nowrap hidden sm:inline">Avvio rapido:</span>
              <div className="relative flex-1 max-w-xs">
                <input
                  type="text"
                  value={agentFilter}
                  onChange={(e) => setAgentFilter(e.target.value)}
                  placeholder="Filtra specialisti..."
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {agentFilter && (
                  <button
                    onClick={() => setAgentFilter('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            {/* Row 1 - Original agents */}
            <div className="flex items-center gap-2 overflow-x-auto scrollbar-none pb-1">
              {agents
                .filter(agent => {
                  const originalAgents = ['coordinator', 'oncologist_colon', 'oncologist_prostate', 'oncologist_lung', 'oncologist_bone', 'cardiologist', 'nutritionist', 'trainer', 'longevity', 'pharmacist'];
                  return originalAgents.includes(agent.id);
                })
                .filter(agent => {
                  if (!agentFilter.trim()) return true;
                  const filter = agentFilter.toLowerCase();
                  const label = AGENT_SHORT_LABELS[agent.id] || agent.name;
                  return label.toLowerCase().includes(filter) || agent.name.toLowerCase().includes(filter) || agent.specialization?.toLowerCase().includes(filter);
                })
                .map((agent) => (
                <button
                  key={agent.id}
                  onClick={() => handleNewChat(agent.id)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all whitespace-nowrap flex-shrink-0 ${
                    currentConversation?.agentType === agent.id ? 'bg-blue-50 border-blue-300' : 'bg-white'
                  }`}
                  title={agent.description}
                >
                  <span className={`w-6 h-6 ${AGENT_COLORS[agent.id] || 'bg-gray-500'} rounded-full flex items-center justify-center text-white text-sm`}>
                    {AGENT_ICONS[agent.id] || '🤖'}
                  </span>
                  <span className="text-sm font-medium text-gray-700">{AGENT_SHORT_LABELS[agent.id] || agent.name}</span>
                </button>
              ))}
            </div>
            {/* Row 2 - New specialist agents */}
            <div className="flex items-center gap-2 overflow-x-auto scrollbar-none pt-1">
              {agents
                .filter(agent => {
                  const newAgents = ['urologist', 'pulmonologist', 'orthopedist', 'ophthalmologist', 'gastroenterologist', 'dermatologist', 'rheumatologist', 'orthodontist', 'otolaryngologist'];
                  return newAgents.includes(agent.id);
                })
                .filter(agent => {
                  if (!agentFilter.trim()) return true;
                  const filter = agentFilter.toLowerCase();
                  const label = AGENT_SHORT_LABELS[agent.id] || agent.name;
                  return label.toLowerCase().includes(filter) || agent.name.toLowerCase().includes(filter) || agent.specialization?.toLowerCase().includes(filter);
                })
                .map((agent) => (
                <button
                  key={agent.id}
                  onClick={() => handleNewChat(agent.id)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all whitespace-nowrap flex-shrink-0 ${
                    currentConversation?.agentType === agent.id ? 'bg-blue-50 border-blue-300' : 'bg-white'
                  }`}
                  title={agent.description}
                >
                  <span className={`w-6 h-6 ${AGENT_COLORS[agent.id] || 'bg-gray-500'} rounded-full flex items-center justify-center text-white text-sm`}>
                    {AGENT_ICONS[agent.id] || '🤖'}
                  </span>
                  <span className="text-sm font-medium text-gray-700">{AGENT_SHORT_LABELS[agent.id] || agent.name}</span>
                </button>
              ))}
            </div>
            {/* No results message */}
            {agentFilter.trim() && agents.filter(agent => {
              const filter = agentFilter.toLowerCase();
              const label = AGENT_SHORT_LABELS[agent.id] || agent.name;
              return label.toLowerCase().includes(filter) || agent.name.toLowerCase().includes(filter) || agent.specialization?.toLowerCase().includes(filter);
            }).length === 0 && (
              <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <span className="font-medium">Nessuno specialista trovato per "{agentFilter}"</span>
                <p className="mt-1 text-amber-700">
                  Manca lo specialista che stai cercando? Fai richiesta scrivendo al supporto:{' '}
                  <a href="mailto:health.assistance.tool@gmail.com" className="underline hover:text-amber-900 font-medium">
                    health.assistance.tool@gmail.com
                  </a>
                </p>
              </div>
            )}
          </div>

          {/* Chat header - sticky on mobile */}
          {currentConversation && (
            <div className="bg-white border-b border-gray-200 px-4 lg:px-8 py-3 lg:py-4 flex items-center gap-3 lg:gap-4 sticky top-0 z-10 flex-shrink-0">
              {/* Mobile toggle for conversation list */}
              {isMobile && (
                <button
                  onClick={() => setShowMobileSidebar(true)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                  title="Mostra conversazioni"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                  </svg>
                </button>
              )}
              <span
                className={`w-10 h-10 lg:w-14 lg:h-14 ${
                  AGENT_COLORS[currentConversation.agentType] || 'bg-gray-500'
                } rounded-full flex items-center justify-center text-white text-lg lg:text-2xl flex-shrink-0`}
              >
                {AGENT_ICONS[currentConversation.agentType] || '💬'}
              </span>
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold text-base lg:text-xl text-gray-900 truncate">{currentConversation.title}</h2>
                <div className="flex items-center gap-2">
                  <p className="text-sm lg:text-lg text-gray-500 truncate">
                    {agents.find((a) => a.id === currentConversation.agentType)?.name ||
                      currentConversation.agentType}
                  </p>
                  {lastLLMInfo && (
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        lastLLMInfo.usingFallback
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                      title={`Provider: ${lastLLMInfo.provider} | Modello: ${lastLLMInfo.model}${lastLLMInfo.usingFallback ? ' (Ollama non disponibile)' : ''}`}
                    >
                      {lastLLMInfo.usingFallback ? '⚡ ' : ''}{lastLLMInfo.provider}
                    </span>
                  )}
                </div>
              </div>
              {/* Export menu */}
              <div className="relative" ref={exportMenuRef}>
                <button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  className="p-2 lg:p-3 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
                  title="Esporta conversazione"
                  disabled={messages.length === 0}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                  </svg>
                </button>
                {showExportMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                    <button
                      onClick={handleCopyAsText}
                      className="w-full px-4 py-3 text-left text-base hover:bg-gray-50 flex items-center gap-3"
                    >
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                      </svg>
                      Copia come testo
                    </button>
                    <button
                      onClick={handleCopyAsMarkdown}
                      className="w-full px-4 py-3 text-left text-base hover:bg-gray-50 flex items-center gap-3"
                    >
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                      </svg>
                      Copia come Markdown
                    </button>
                    <div className="border-t border-gray-100 my-1"></div>
                    <button
                      onClick={handleDownloadMarkdown}
                      className="w-full px-4 py-3 text-left text-base hover:bg-gray-50 flex items-center gap-3"
                    >
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Scarica Markdown (.md)
                    </button>
                    <button
                      onClick={handleExportPdf}
                      disabled={isExporting}
                      className="w-full px-4 py-3 text-left text-base hover:bg-gray-50 flex items-center gap-3 disabled:opacity-50"
                    >
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      {isExporting ? 'Esportazione...' : 'Esporta PDF'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto p-4 lg:p-8">
            {!currentConversation ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <div className="text-5xl lg:text-6xl mb-6">💬</div>
                  <h2 className="text-xl lg:text-3xl font-bold text-gray-700 mb-3">
                    Benvenuto nella Chat
                  </h2>
                  <p className="text-base lg:text-xl text-gray-500 mb-6 lg:mb-8">
                    {isMobile ? 'Inizia una nuova conversazione' : 'Seleziona una conversazione o iniziane una nuova'}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    {isMobile && conversations.length > 0 && (
                      <button
                        onClick={() => setShowMobileSidebar(true)}
                        className="px-6 py-3 lg:py-4 text-base lg:text-lg border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
                      >
                        Vedi conversazioni ({conversations.length})
                      </button>
                    )}
                    <button
                      onClick={() => setShowNewChat(true)}
                      className="px-6 lg:px-8 py-3 lg:py-4 text-base lg:text-lg bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                    >
                      Nuova Chat
                    </button>
                  </div>
                </div>
              </div>
            ) : isLoading ? (
              <div className="h-full flex items-center justify-center">
                <p className="text-lg lg:text-xl text-gray-500">Caricamento...</p>
              </div>
            ) : messages.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <p className="text-lg lg:text-xl text-gray-500">
                  Inizia la conversazione scrivendo un messaggio
                </p>
              </div>
            ) : (
              <div className="space-y-5 max-w-4xl mx-auto">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`group flex ${
                      msg.role === 'user' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    {/* Edit button for user messages - shown on left */}
                    {msg.role === 'user' && !isSending && (
                      <button
                        onClick={() => handleEditMessage(msg)}
                        className="self-center mr-2 p-2 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-blue-600 transition-opacity"
                        title="Modifica messaggio"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                    )}
                    <div
                      className={`relative max-w-[80%] rounded-2xl px-5 lg:px-6 py-4 ${
                        msg.role === 'user'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white border border-gray-200 shadow-sm'
                      }`}
                    >
                      {msg.role === 'assistant' && msg.agentType && (
                        <div className="flex items-center gap-2 mb-3 text-base lg:text-lg text-gray-500">
                          <span className="text-lg lg:text-xl">{AGENT_ICONS[msg.agentType] || '🤖'}</span>
                          <span>
                            {agents.find((a) => a.id === msg.agentType)?.name ||
                              msg.agentType}
                          </span>
                        </div>
                      )}
                      <div className={`prose prose-sm lg:prose-base max-w-none prose-headings:mt-3 prose-headings:mb-2 prose-p:my-1 prose-ul:my-1 prose-li:my-0 ${
                        msg.role === 'user'
                          ? 'prose-invert prose-p:text-white prose-headings:text-white prose-strong:text-white prose-li:text-white'
                          : ''
                      }`}>
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <p
                          className={`text-sm lg:text-base ${
                            msg.role === 'user' ? 'text-blue-200' : 'text-gray-400'
                          }`}
                        >
                          {new Date(msg.createdAt).toLocaleTimeString('it-IT', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                        {/* Copy message menu */}
                        <div className="message-menu-container relative">
                          <button
                            onClick={() => setMessageMenuId(messageMenuId === msg.id ? null : msg.id)}
                            className={`p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity ${
                              msg.role === 'user'
                                ? 'text-blue-200 hover:text-white hover:bg-blue-500'
                                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                            }`}
                            title="Copia messaggio"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                            </svg>
                          </button>
                          {messageMenuId === msg.id && (
                            <div className={`absolute z-50 mt-1 w-44 bg-white rounded-xl shadow-lg border border-gray-200 py-1 ${
                              msg.role === 'user' ? 'right-0' : 'left-0'
                            }`}>
                              <button
                                onClick={() => {
                                  handleCopyMessage(msg, 'text');
                                  setMessageMenuId(null);
                                }}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                              >
                                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Copia come testo
                              </button>
                              <button
                                onClick={() => {
                                  handleCopyMessage(msg, 'markdown');
                                  setMessageMenuId(null);
                                }}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                              >
                                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                                Copia Markdown
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {isSending && (
                  <div className="flex justify-start">
                    <div className="bg-white border border-gray-200 shadow-sm rounded-2xl px-5 lg:px-6 py-4">
                      <div className="flex items-center gap-2 text-gray-500 text-lg">
                        <span className="animate-pulse">●</span>
                        <span className="animate-pulse delay-100">●</span>
                        <span className="animate-pulse delay-200">●</span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input area */}
          {currentConversation && (
            <div className="bg-white border-t border-gray-200 p-4 lg:p-6">
              <div className="max-w-4xl mx-auto">
                {/* Editing indicator */}
                {editingMessageId && (
                  <div className="flex items-center gap-2 mb-3 text-blue-600 bg-blue-50 rounded-lg px-3 py-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span className="text-sm font-medium">Modifica messaggio</span>
                    <button
                      onClick={handleCancelEdit}
                      className="ml-auto text-gray-500 hover:text-red-500"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
                {/* Attachments preview */}
                {attachments.length > 0 && (
                  <div className="mb-3">
                    <div className="flex flex-wrap gap-2">
                      {attachments.map((att, index) => (
                        <div
                          key={index}
                          className={`flex items-center gap-2 rounded-lg px-3 py-2 ${
                            att.type === 'image' && visionWarning
                              ? 'bg-amber-50 border border-amber-200'
                              : 'bg-gray-100'
                          }`}
                        >
                          <span className="text-base">
                            {att.type === 'image' ? '📷' : '📄'}
                          </span>
                          <span className="text-sm text-gray-700 max-w-32 truncate">
                            {att.filename}
                          </span>
                          {att.type === 'image' && visionWarning && (
                            <span className="text-amber-500" title="Modello non supporta immagini">⚠️</span>
                          )}
                          <button
                            onClick={() => removeAttachment(index)}
                            className="text-gray-400 hover:text-red-500"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                    {visionWarning && attachments.some(a => a.type === 'image') && (
                      <p className="text-sm text-amber-600 mt-2">
                        ⚠️ Il modello {visionWarning.model} non supporta le immagini
                      </p>
                    )}
                  </div>
                )}
                <div className="flex gap-3 lg:gap-4">
                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,.pdf,.txt,.json,.md"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  {/* Attachment button */}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-4 py-3 lg:py-4 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                    title="Allega file"
                    disabled={isSending}
                  >
                    <svg className="w-5 h-5 lg:w-6 lg:h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                  </button>
                  <textarea
                    ref={inputRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Scrivi un messaggio..."
                    rows={1}
                    className="flex-1 resize-none border border-gray-300 rounded-xl px-4 lg:px-5 py-3 lg:py-4 text-base lg:text-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isSending}
                  />
                  <button
                    onClick={() => handleSendMessage()}
                    disabled={(!inputValue.trim() && attachments.length === 0) || isSending}
                    className="px-6 lg:px-8 py-3 lg:py-4 text-base lg:text-lg bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {editingMessageId ? 'Aggiorna' : 'Invia'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New chat modal */}
      {showNewChat && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 lg:p-8 max-w-5xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl lg:text-2xl font-bold text-gray-900">Scegli un assistente</h3>
              <button
                onClick={() => setShowNewChat(false)}
                className="text-gray-400 hover:text-gray-600 p-2"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
              {agents.map((agent) => (
                <button
                  key={agent.id}
                  onClick={() => handleNewChat(agent.id)}
                  className="flex-shrink-0 w-40 lg:w-48 p-4 lg:p-5 border border-gray-200 rounded-2xl text-center hover:bg-gray-50 hover:border-blue-300 hover:shadow-md transition-all"
                >
                  <span
                    className={`inline-flex items-center justify-center w-14 h-14 lg:w-16 lg:h-16 ${
                      AGENT_COLORS[agent.id] || 'bg-gray-500'
                    } rounded-full text-white text-2xl lg:text-3xl mb-3`}
                  >
                    {AGENT_ICONS[agent.id] || '🤖'}
                  </span>
                  <p className="font-semibold text-base lg:text-lg text-gray-900 truncate">{agent.name}</p>
                  <p className="text-sm lg:text-base text-gray-500 truncate">{agent.specialization}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Vision warning modal */}
      {showVisionWarning && visionWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 lg:p-8 max-w-lg w-full mx-4">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center text-3xl">
                ⚠️
              </div>
              <div>
                <h3 className="text-xl lg:text-2xl font-bold text-gray-900">
                  Modello non supporta immagini
                </h3>
                <p className="text-base text-gray-500">
                  {visionWarning.provider} / {visionWarning.model}
                </p>
              </div>
            </div>

            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-base lg:text-lg text-amber-800">
                {visionWarning.reason}
              </p>
            </div>

            {visionWarning.suggestions.length > 0 && (
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">Come risolvere:</h4>
                <ul className="space-y-2">
                  {visionWarning.suggestions.map((suggestion, index) => (
                    <li key={index} className="flex items-start gap-2 text-gray-700">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>{suggestion}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowVisionWarning(false);
                  // Remove image attachments and send only text
                  const textAttachments = attachments.filter(a => a.type !== 'image');
                  setAttachments(textAttachments);
                  if (inputValue.trim() || textAttachments.length > 0) {
                    handleSendMessage(true);
                  }
                }}
                className="flex-1 py-3 px-4 text-base lg:text-lg bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Invia solo testo
              </button>
              <button
                onClick={() => {
                  setShowVisionWarning(false);
                  handleSendMessage(true);
                }}
                className="flex-1 py-3 px-4 text-base lg:text-lg bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors"
              >
                Invia comunque
              </button>
            </div>

            <button
              onClick={() => setShowVisionWarning(false)}
              className="w-full mt-3 py-3 px-4 text-base text-gray-500 hover:text-gray-700"
            >
              Annulla
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
}
